import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  db,
  collection,
  query,
  where,
  getDocs,
  functions,
  httpsCallable,
} from "../firebaseConfig";

const ChangeClientNameDialog = ({
  open,
  onClose,
  clientId,
  currentName,
  onSuccess,
}) => {
  const [value, setValue] = useState(currentName || "");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setValue(currentName || "");
    setError("");
  }, [currentName, open]);

  const checkNameAvailability = useCallback(
    async (name) => {
      const lowerValue = name.toLowerCase();
      const snapshot = await getDocs(
        query(
          collection(db, "ClientDatabase"),
          where("brandLowerCase", "==", lowerValue)
        )
      );

      return snapshot.docs.some((doc) => doc.id !== clientId);
    },
    [clientId]
  );

  const handleClose = () => {
    if (submitting) {
      return;
    }
    onClose?.();
  };

  const handleSubmit = async () => {
    const trimmedName = (value || "").trim();
    if (!trimmedName) {
      setError("Please enter a client name.");
      return;
    }

    if (!clientId) {
      setError("Client data is not loaded yet.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const nameTaken = await checkNameAvailability(trimmedName);
      if (nameTaken) {
        setError("Another client already has that name.");
        setSubmitting(false);
        return;
      }

      const renameClientBrand = httpsCallable(functions, "renameClientBrand");
      await renameClientBrand({
        clientId,
        oldBrand: currentName || "",
        newBrand: trimmedName,
      });

      setSubmitting(false);
      onSuccess?.(trimmedName);
      onClose?.();
    } catch (updateError) {
      const message =
        updateError?.code === "already-exists"
          ? "Another client already has that name."
          : updateError?.message || "Failed to change the client name.";
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={submitting}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Change Client Name</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          This will update every job that references the client. The process may
          take a moment, so please wait until it completes before closing the
          page.
        </Typography>
         <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          This will break existing Dropbox links, so please rename the folder in Dropbox to match the new name.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Client Name"
          value={value}
          disabled={submitting}
          onChange={(event) => {
            setValue(event.target.value);
            if (error) {
              setError("");
            }
          }}
          helperText={error || ""}
          error={Boolean(error)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={submitting}
          onClick={handleSubmit}
          endIcon={
            submitting ? (
              <CircularProgress color="inherit" size={16} />
            ) : undefined
          }
        >
          {submitting ? "Saving..." : "Update Name"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeClientNameDialog;
